import {
  packHtmlElementsToPdfBlob,
  blobToBase64,
  downloadBlob,
} from '../../../lib/pdfSectionPacker';

export { blobToBase64, downloadBlob };

/** Navy header bars — CRM secondary token (#1B4C87). */
const HEADER_BG = '#1B4C87';
const ACCENT = '#00AAFF';

const baseSections = [
  {
    title: 'Disclosure & Privacy',
    fields: [
      ['Communication Consent', 'consent_communication'],
      ['Statement Confirmed', 'consent_truthful_information'],
    ],
  },
  {
    title: 'Personal Information',
    fields: [
      ['Full Name', 'full_name'],
      ['Birth State/Country', 'birth_state_country'],
      ['Birth Date', 'birth_date'],
      ['Gender', 'gender'],
      ['SSN/ITIN', 'ssn'],
      ['Address', 'address'],
      ['Years At Address', 'years_at_address'],
      ['Cell Phone', 'cell_phone'],
      ['Email', 'email'],
      ['Citizenship', 'citizenship'],
      ['ID Type', 'id_type'],
      ['ID Number', 'id_number'],
      ['ID State', 'id_state'],
      ['ID Expiration', 'id_expiration'],
    ],
  },
  {
    title: 'Employment',
    fields: [
      ['Employer Name', 'employer_name'],
      ['Employer Address', 'employer_address'],
      ['Occupation', 'occupation'],
      ['Duties', 'duties'],
      ['Employer Phone', 'employer_phone'],
      ['Years At Employer', 'years_at_employer'],
    ],
  },
  {
    title: 'Financial Information',
    fields: [
      ['Annual Income', 'annual_income'],
      ['Household Income', 'household_income'],
      ['Monthly Expenses', 'monthly_expenses'],
      ['Net Worth', 'net_worth'],
    ],
  },
  {
    title: 'Insurance Information',
    fields: [
      ['Insurance Type', 'insurance_type'],
      ['Monthly Premium', 'monthly_premium'],
      ['Insurance Option', 'insurance_option'],
      ['Indexed Strategy 1', 'indexed_strategy_1'],
      ['Indexed Strategy 2', 'indexed_strategy_2'],
      ['Existing Policies', 'existing_policies'],
    ],
  },
  {
    title: 'Bank Information',
    fields: [
      ['Bank Name', 'bank_name'],
      ['Routing Number', 'routing_number'],
      ['Account Number', 'account_number'],
    ],
  },
  {
    title: 'Illustration / Quote',
    fields: [['Rate Class', 'rate_class']],
  },
  {
    title: 'Health Questionnaire (Primary Insured)',
    fields: [
      ['Health Provider', 'health_provider'],
      ['Last Medical Visit', 'last_medical_visit'],
      ['Height', 'height'],
      ['Weight', 'weight'],
      ['Weight Change 10lbs', 'weight_change_10lbs'],
      ['Is Smoker', 'is_smoker'],
      ['Medications 5yr', 'medications_5yr'],
      ['Other Specialists 5yr', 'other_specialists_5yr'],
      ['Other Specialists Detail', 'other_specialists_detail'],
      ['Father Details', 'father_details'],
      ['Mother Details', 'mother_details'],
      ['Siblings Details', 'siblings_details'],
      ['International Travel', 'international_travel'],
      ['Dangerous Activities', 'dangerous_activities'],
      ['Unpaid Tickets', 'unpaid_tickets'],
      ['Exercises Regularly', 'exercises_regularly'],
      ['Health Conditions', 'health_conditions'],
    ],
  },
  {
    title: 'Agent Comments',
    fields: [
      ['Agent Name/Code', 'agent_name_code'],
      ['Split Agent Name/Code', 'split_agent_name_code'],
      ['Agent Carrier', 'agent_carrier'],
      ['Agent Indexed Strategies', 'agent_indexed_strategies'],
      ['Agent Other Comments', 'agent_other_comments'],
    ],
  },
];

const spouseSection = {
  title: 'Spouse / Secondary Insured',
  fields: [
    ['Spouse Application Type', 'spouse_application_type'],
    ['Spouse Full Name', 'spouse_full_name'],
    ['Spouse Birth Date', 'spouse_birth_date'],
    ['Spouse Gender', 'spouse_gender'],
    ['Spouse SSN', 'spouse_ssn'],
    ['Spouse Citizenship', 'spouse_citizenship'],
    ['Spouse ID Type', 'spouse_id_type'],
    ['Spouse ID Number', 'spouse_id_number'],
    ['Spouse ID State', 'spouse_id_state'],
    ['Spouse ID Expiration', 'spouse_id_expiration'],
    ['Spouse Coverage Amount', 'spouse_coverage_amount'],
    ['Spouse Coverage Type', 'spouse_coverage_type'],
    ['Spouse Health Provider', 'spouse_health_provider'],
    ['Spouse Last Medical Visit', 'spouse_last_medical_visit'],
    ['Spouse Height', 'spouse_height'],
    ['Spouse Weight', 'spouse_weight'],
    ['Spouse Weight Change 10lbs', 'spouse_weight_change_10lbs'],
    ['Spouse Is Smoker', 'spouse_is_smoker'],
    ['Spouse Medications 5yr', 'spouse_medications_5yr'],
    ['Spouse Other Specialists', 'spouse_other_specialists_5yr'],
    ['Spouse Other Specialists Detail', 'spouse_other_specialists_detail'],
    ['Spouse International Travel', 'spouse_international_travel'],
    ['Spouse Dangerous Activities', 'spouse_dangerous_activities'],
    ['Spouse Unpaid Tickets', 'spouse_unpaid_tickets'],
    ['Spouse Exercises Regularly', 'spouse_exercises_regularly'],
    ['Spouse Health Conditions', 'spouse_health_conditions'],
  ],
};

function applicationTypeSuffix(type) {
  if (type === 'standalone') return ' (Standalone Policy)';
  if (type === 'rider') return ' (Rider)';
  return '';
}

const valueToString = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  if (Array.isArray(value)) {
    if (!value.length) return '-';
    return value.map((entry) => valueToString(entry)).join(', ');
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

function createSectionElement(section, record) {
  const sectionNode = document.createElement('section');
  sectionNode.style.width = '930px';
  sectionNode.style.marginBottom = '16px';
  sectionNode.style.background = '#FFFFFF';
  sectionNode.style.border = '1px solid #E2E8F0';
  sectionNode.style.borderRadius = '10px';
  sectionNode.style.overflow = 'hidden';

  const titleNode = document.createElement('div');
  titleNode.style.background = HEADER_BG;
  titleNode.style.color = '#FFFFFF';
  titleNode.style.padding = '10px 14px';
  titleNode.style.fontWeight = '700';
  titleNode.style.fontSize = '14px';
  titleNode.textContent = section.title;
  sectionNode.appendChild(titleNode);

  const bodyNode = document.createElement('div');
  bodyNode.style.display = 'grid';
  bodyNode.style.gridTemplateColumns = '1fr 1fr';
  bodyNode.style.gap = '10px 18px';
  bodyNode.style.padding = '14px';

  section.fields.forEach(([label, key]) => {
    const fieldWrap = document.createElement('div');
    const labelNode = document.createElement('div');
    labelNode.style.fontSize = '11px';
    labelNode.style.fontWeight = '700';
    labelNode.style.color = '#475569';
    labelNode.textContent = label;

    const valueNode = document.createElement('div');
    valueNode.style.marginTop = '3px';
    valueNode.style.fontSize = '12px';
    valueNode.style.color = '#0F172A';
    valueNode.style.whiteSpace = 'pre-wrap';
    valueNode.textContent = valueToString(record[key]);

    fieldWrap.appendChild(labelNode);
    fieldWrap.appendChild(valueNode);
    bodyNode.appendChild(fieldWrap);
  });

  sectionNode.appendChild(bodyNode);
  return sectionNode;
}

function createBeneficiariesElement(beneficiaries = []) {
  const sectionNode = document.createElement('section');
  sectionNode.style.width = '930px';
  sectionNode.style.marginBottom = '16px';
  sectionNode.style.background = '#FFFFFF';
  sectionNode.style.border = '1px solid #E2E8F0';
  sectionNode.style.borderRadius = '10px';
  sectionNode.style.overflow = 'hidden';

  const titleNode = document.createElement('div');
  titleNode.style.background = HEADER_BG;
  titleNode.style.color = '#FFFFFF';
  titleNode.style.padding = '10px 14px';
  titleNode.style.fontWeight = '700';
  titleNode.style.fontSize = '14px';
  titleNode.textContent = 'Beneficiaries';
  sectionNode.appendChild(titleNode);

  const bodyNode = document.createElement('div');
  bodyNode.style.padding = '14px';

  if (!beneficiaries.length) {
    bodyNode.textContent = 'No beneficiaries listed.';
    bodyNode.style.fontSize = '12px';
    bodyNode.style.color = '#64748B';
    sectionNode.appendChild(bodyNode);
    return sectionNode;
  }

  beneficiaries.forEach((beneficiary, idx) => {
    const row = document.createElement('div');
    row.style.padding = '10px';
    row.style.border = '1px solid #E2E8F0';
    row.style.borderRadius = '8px';
    row.style.marginBottom = '10px';
    row.style.fontSize = '12px';
    row.style.whiteSpace = 'pre-wrap';
    row.textContent = `#${idx + 1} ${valueToString(beneficiary.full_name)} | DOB: ${valueToString(beneficiary.dob)} | SSN: ${valueToString(beneficiary.ssn)} | Relationship: ${valueToString(beneficiary.relationship)} | Percentage: ${valueToString(beneficiary.percentage)}`;
    bodyNode.appendChild(row);
  });

  sectionNode.appendChild(bodyNode);
  return sectionNode;
}

function createChildrenElement(children = []) {
  const sectionNode = document.createElement('section');
  sectionNode.style.width = '930px';
  sectionNode.style.marginBottom = '16px';
  sectionNode.style.background = '#FFFFFF';
  sectionNode.style.border = '1px solid #E2E8F0';
  sectionNode.style.borderRadius = '10px';
  sectionNode.style.overflow = 'hidden';

  const titleNode = document.createElement('div');
  titleNode.style.background = HEADER_BG;
  titleNode.style.color = '#FFFFFF';
  titleNode.style.padding = '10px 14px';
  titleNode.style.fontWeight = '700';
  titleNode.style.fontSize = '14px';
  titleNode.textContent = 'Children';
  sectionNode.appendChild(titleNode);

  const bodyNode = document.createElement('div');
  bodyNode.style.padding = '14px';

  if (!children.length) {
    bodyNode.textContent = 'No children listed.';
    bodyNode.style.fontSize = '12px';
    bodyNode.style.color = '#64748B';
    sectionNode.appendChild(bodyNode);
    return sectionNode;
  }

  children.forEach((child, idx) => {
    const row = document.createElement('div');
    row.style.padding = '10px';
    row.style.border = '1px solid #E2E8F0';
    row.style.borderRadius = '8px';
    row.style.marginBottom = '10px';
    row.style.fontSize = '12px';
    row.style.whiteSpace = 'pre-wrap';
    row.textContent = `#${idx + 1} ${valueToString(child.full_name)}${applicationTypeSuffix(child.application_type)} | DOB: ${valueToString(child.dob)} | Gender: ${valueToString(child.gender)} | SSN: ${valueToString(child.ssn)} | Height: ${valueToString(child.height)} | Weight: ${valueToString(child.weight)} | Conditions: ${valueToString(child.known_conditions)} | Medications: ${valueToString(child.medications)}`;
    bodyNode.appendChild(row);
  });

  sectionNode.appendChild(bodyNode);
  return sectionNode;
}

/** Generate a multi-page PDF blob.
 *  Sections flow continuously; page breaks only when the next section
 *  would not fit in the remaining vertical space (sections are never split). */
export async function generatePdfBlob(application) {
  const root = document.createElement('div');
  root.style.position = 'fixed';
  root.style.left = '-100000px';
  root.style.top = '0';
  root.style.width = '960px';
  root.style.background = '#FFFFFF';
  root.style.padding = '16px';
  root.style.fontFamily = 'Arial, sans-serif';
  root.style.color = '#0F172A';
  document.body.appendChild(root);

  try {
    const header = document.createElement('div');
    header.style.marginBottom = '14px';
    header.style.padding = '14px';
    header.style.border = `2px solid ${ACCENT}`;
    header.style.borderRadius = '10px';
    header.innerHTML = `
      <div style="font-size: 21px; font-weight: 800; color: ${HEADER_BG};">Life Insurance Pre-Underwriting (US)</div>
      <div style="font-size: 14px; font-weight: 700; color: ${ACCENT}; margin-top: 4px;">Prosperity Leaders</div>
    `;
    root.appendChild(header);

    const nodes = [header];

    for (const section of baseSections) {
      const node = createSectionElement(section, application);
      root.appendChild(node);
      nodes.push(node);
    }

    if (application?.has_spouse) {
      const spouseForPdf = {
        ...spouseSection,
        title: `Spouse / Secondary Insured${applicationTypeSuffix(application.spouse_application_type)}`,
        fields: spouseSection.fields.map(([label, key]) => {
          if (key === 'spouse_application_type') {
            return [label, '__spouse_application_type_label'];
          }
          return [label, key];
        }),
      };
      const record = {
        ...application,
        __spouse_application_type_label:
          application.spouse_application_type === 'standalone'
            ? 'Standalone Policy'
            : application.spouse_application_type === 'rider'
              ? 'Rider'
              : application.spouse_application_type || '-',
      };
      const spouseNode = createSectionElement(spouseForPdf, record);
      root.appendChild(spouseNode);
      nodes.push(spouseNode);
    }

    const beneficiariesNode = createBeneficiariesElement(application?.beneficiaries || []);
    root.appendChild(beneficiariesNode);
    nodes.push(beneficiariesNode);

    if (
      application?.has_children &&
      Array.isArray(application?.children) &&
      application.children.length
    ) {
      const childrenNode = createChildrenElement(application.children);
      root.appendChild(childrenNode);
      nodes.push(childrenNode);
    }

    // Must await: finally removes `root` from the document; html2canvas requires
    // capture targets to remain attached until cloning finishes.
    return await packHtmlElementsToPdfBlob(nodes);
  } finally {
    if (root.parentNode) {
      root.parentNode.removeChild(root);
    }
  }
}
